import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { InvoiceDisplayStatusV2, ISSUES_PREVENTING_PAYMENT } from 'anchor-scout-common/types';
import { getTestIds } from 'seagull/utils/TestIds';
import GenericError from '~/components/GenericError/GenericError';
import { routes } from '~/constants';
import { useBillingCacheSubscriber } from '~/hooks/useBillingCacheSubscriber';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { InvoicesDigestModel } from '~/models/invoices-digest/InvoicesDigestModel';
import GraphWidgetBottomCard from '~/pages/VendorDashboard/GraphWidget/GraphWidgetBottomCard';
import GraphWidgetLayout from '~/pages/VendorDashboard/GraphWidget/GraphWidgetLayout';
import InvoiceBar from '~/pages/VendorDashboard/InvoicesDigestWidget/InvoiceBar';
import { InvoicesInfoTable } from '~/pages/VendorDashboard/InvoicesDigestWidget/InvoicesDigestTable';
import InvoicesInfoTableOld from '~/pages/VendorDashboard/InvoicesDigestWidget/InvoicesDigestTableOld';
import ThreeLineTopCard from '../GraphWidget/ThreeLineTopCard';
import { useInvoicesDigest } from './hooks/useInvoicesDigest';
import styles from './InvoicesDigestWidget.module.scss';
const makeNewInvoiceStatusGroup = (count, amount)=>{
    return {
        count,
        amount: {
            amount
        }
    };
};
export function mockInvoiceDigestApi() {
    let { hasCreatedInvoice } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {
        hasCreatedInvoice: true
    };
    return {
        statuses: {
            requiresClientApproval: makeNewInvoiceStatusGroup(10, 1500000),
            paymentsVerificationRequired: makeNewInvoiceStatusGroup(10, 1500000),
            missingPaymentMethod: makeNewInvoiceStatusGroup(10, 1500000),
            failed: makeNewInvoiceStatusGroup(10, 1500000),
            disputed: makeNewInvoiceStatusGroup(10, 1500000),
            paymentStopped: makeNewInvoiceStatusGroup(0, 0),
            paymentMethodUnverified: makeNewInvoiceStatusGroup(10, 1500000)
        },
        processing: makeNewInvoiceStatusGroup(123, 12300),
        requiresAttention: makeNewInvoiceStatusGroup(123, 12300),
        hasCreatedInvoice: !!hasCreatedInvoice
    };
}
function MockInvoicesDigestBottomCard() {
    const invoiceDigest = useMemo(()=>InvoicesDigestModel.fromDTO(mockInvoiceDigestApi()), []);
    return /*#__PURE__*/ _jsx(BottomCardContent, {
        invoicesDigest: invoiceDigest
    });
}
const BottomCardContent = (param)=>{
    let { invoicesDigest } = param;
    const { SEAGULL_TABLES: isSeagullTablesEnabled } = useFeatureFlags();
    const { InvoicesDigestWidgetTID } = useMemo(()=>GetInvoicesDigestWidgetTID(), []);
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.bottomCardContent, {
            [styles.scrollsTableInCard]: isSeagullTablesEnabled
        }),
        "data-testid": InvoicesDigestWidgetTID.BottomCardContent(),
        children: [
            /*#__PURE__*/ _jsx(InvoiceBar, {
                invoicesDigest: invoicesDigest
            }),
            isSeagullTablesEnabled ? /*#__PURE__*/ _jsx(InvoicesInfoTable, {
                invoiceDigestModel: invoicesDigest
            }) : /*#__PURE__*/ _jsx(InvoicesInfoTableOld, {
                invoiceDigestModel: invoicesDigest
            })
        ]
    });
};
export const GetInvoicesDigestWidgetTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            InvoicesDigestWidgetTID: {
                Error: 'widget-error',
                Processing: 'invoices-digest-processing',
                ProcessingTooltip: 'invoices-digest-processing-tooltip',
                RequireAttention: 'invoices-digest-require-attention',
                RequireAttentionTooltip: 'invoices-digest-require-attention-tooltip',
                BottomCard: 'invoices-digest-bottom-card',
                InvoiceEverCreatedCoveringText: 'invoice-ever-created-covering-text',
                NeverCreatedInvoiceCoveringText: 'never-created-invoice-covering-text'
            }
        }
    });
const InvoicesDigestWidget = (param)=>{
    let { invoicesDigest, isRefreshing, isFirstTimeLoading } = param;
    const { t: invoicesDigestWidgetTexts } = useLocaleKeys('home.dashboard.invoicesDigestWidget');
    const { InvoicesDigestWidgetTID } = useMemo(()=>GetInvoicesDigestWidgetTID(), []);
    const processingLinkTo = "".concat(routes.invoicesDashboard, "?displayStatusV2=").concat(InvoiceDisplayStatusV2.Processing);
    const requireAttentionLinkTo = "".concat(routes.invoicesDashboard, "?paymentDetails=").concat(ISSUES_PREVENTING_PAYMENT.join(','));
    return /*#__PURE__*/ _jsxs(GraphWidgetLayout, {
        loading: isRefreshing,
        title: invoicesDigestWidgetTexts.title(),
        bottomHeight: "l",
        children: [
            /*#__PURE__*/ _jsx(ThreeLineTopCard, {
                loading: isFirstTimeLoading,
                title: invoicesDigestWidgetTexts.processing.title(),
                success: true,
                testId: InvoicesDigestWidgetTID.Processing(),
                titleInfo: /*#__PURE__*/ _jsx("div", {
                    "data-testid": InvoicesDigestWidgetTID.ProcessingTooltip(),
                    children: invoicesDigestWidgetTexts.processing.tooltip()
                }),
                secondaryText: invoicesDigestWidgetTexts.invoicesCount({
                    count: invoicesDigest.processing().count
                }),
                primaryText: invoicesDigest.processing().amount.display(),
                linkTo: processingLinkTo
            }),
            /*#__PURE__*/ _jsx(ThreeLineTopCard, {
                loading: isFirstTimeLoading,
                title: invoicesDigestWidgetTexts.requireAttention.title(),
                testId: InvoicesDigestWidgetTID.RequireAttention(),
                secondaryText: invoicesDigestWidgetTexts.invoicesCount({
                    count: invoicesDigest.requiresAttention().count
                }),
                titleInfo: /*#__PURE__*/ _jsx("div", {
                    "data-testid": InvoicesDigestWidgetTID.RequireAttentionTooltip(),
                    children: invoicesDigestWidgetTexts.requireAttention.tooltip()
                }),
                primaryText: invoicesDigest.requiresAttention().amount.display(),
                linkTo: requireAttentionLinkTo
            }),
            /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                testId: InvoicesDigestWidgetTID.BottomCard(),
                loadingOrEmptyBg: ()=>!invoicesDigest.hasCreatedInvoice() || isFirstTimeLoading ? /*#__PURE__*/ _jsx(MockInvoicesDigestBottomCard, {}) : null,
                loading: isFirstTimeLoading,
                loadingMsg: invoicesDigestWidgetTexts.loadingMsg(),
                empty: invoicesDigest.numInvoiceTypes() === 0,
                emptyMsg: invoicesDigest.hasCreatedInvoice() ? /*#__PURE__*/ _jsx("div", {
                    "data-testid": InvoicesDigestWidgetTID.InvoiceEverCreatedCoveringText(),
                    children: invoicesDigestWidgetTexts.noAttentionRequired()
                }) : /*#__PURE__*/ _jsx("div", {
                    "data-testid": InvoicesDigestWidgetTID.NeverCreatedInvoiceCoveringText(),
                    children: invoicesDigestWidgetTexts.viewAttentionRequired()
                }),
                children: /*#__PURE__*/ _jsx(BottomCardContent, {
                    invoicesDigest: invoicesDigest
                })
            })
        ]
    });
};
export default function InvoicesDigestWidgetFetch() {
    const { InvoicesDigestWidgetTID } = useMemo(()=>GetInvoicesDigestWidgetTID(), []);
    const { invoicesDigest, isLoading, isBackgroundLoading, refresh, error } = useInvoicesDigest({
        refreshOnHookMount: true
    });
    useBillingCacheSubscriber(async ()=>{
        refresh();
    });
    if (error) return /*#__PURE__*/ _jsx(GenericError, {
        testId: InvoicesDigestWidgetTID.Error()
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx(InvoicesDigestWidget, {
            invoicesDigest: InvoicesDigestModel.fromDTO(mockInvoiceDigestApi()),
            isRefreshing: true,
            isFirstTimeLoading: true
        });
    }
    return /*#__PURE__*/ _jsx(InvoicesDigestWidget, {
        invoicesDigest: invoicesDigest,
        isRefreshing: isBackgroundLoading,
        isFirstTimeLoading: false
    });
}
